import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  emailAvailabilityRequestSchema,
  emailAvailabilityResponseSchema,
} from '@shared/schemas/auth';
import { ApiError } from '@/lib/api-error';
import { apiBaseUrl } from '@/lib/auth-client';
import { useDebouncedValue } from './use-debounced-value';

export const EMAIL_ALREADY_EXISTS_MESSAGE = 'Un compte existe déjà avec cette adresse e-mail.';

const EMAIL_AVAILABILITY_DEBOUNCE_MS = 400;
const EMAIL_AVAILABILITY_STALE_TIME_MS = 30_000;

/** `platform`: super admin console; `company`: active company console. */
export type EmailAvailabilityScope = 'company' | 'platform';

const EMAIL_AVAILABILITY_PATHS: Record<EmailAvailabilityScope, string> = {
  company: '/api/company/members/email-availability',
  platform: '/api/admin/users/email-availability',
};

const emailAvailabilityQueryKey = (scope: EmailAvailabilityScope, email: string) => (
  ['email-availability', scope, email] as const
);

function normalizeCheckableEmail(value: string | undefined) {
  const parsed = emailAvailabilityRequestSchema.shape.email.safeParse(value ?? '');
  return parsed.success ? parsed.data : null;
}

async function fetchEmailAvailability(
  scope: EmailAvailabilityScope,
  email: string,
  signal: AbortSignal,
) {
  // POST keeps the address out of URLs and access logs.
  const response = await fetch(new URL(EMAIL_AVAILABILITY_PATHS[scope], apiBaseUrl), {
    body: JSON.stringify({ email }),
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    method: 'POST',
    signal,
  });
  const payload = await response.json().catch(() => null) as {
    error?: { code?: string; message?: string };
  } | null;
  if (!response.ok) {
    throw new ApiError({
      code: payload?.error?.code,
      message: payload?.error?.message ?? response.statusText,
      status: response.status,
    });
  }
  return emailAvailabilityResponseSchema.parse(payload).data.available;
}

/**
 * Checks, while the administrator types, whether an email already belongs to
 * an account. Only a syntactically valid address that stayed unchanged for
 * the debounce delay is sent; a superseded request is aborted and its late
 * answer never applies to the current value. A failed check stays silent:
 * the server still rejects a duplicate on submission.
 */
export function useEmailAvailability(email: string | undefined, scope: EmailAvailabilityScope) {
  const queryClient = useQueryClient();
  const currentEmail = normalizeCheckableEmail(email);
  const debouncedEmail = useDebouncedValue(currentEmail, EMAIL_AVAILABILITY_DEBOUNCE_MS);
  const checkedEmail = debouncedEmail !== null && debouncedEmail === currentEmail
    ? debouncedEmail
    : null;
  const query = useQuery({
    enabled: checkedEmail !== null,
    queryFn: ({ signal }) => fetchEmailAvailability(scope, checkedEmail ?? '', signal),
    queryKey: emailAvailabilityQueryKey(scope, checkedEmail ?? ''),
    retry: false,
    staleTime: EMAIL_AVAILABILITY_STALE_TIME_MS,
  });
  const isTaken = checkedEmail !== null && query.data === false;

  /** Records a duplicate confirmed by the server on submission. */
  function markTaken(value: string) {
    const normalized = normalizeCheckableEmail(value);
    if (normalized) {
      queryClient.setQueryData(emailAvailabilityQueryKey(scope, normalized), false);
    }
  }

  return {
    error: isTaken ? { message: EMAIL_ALREADY_EXISTS_MESSAGE } : undefined,
    isTaken,
    markTaken,
  };
}
